'use client'
import { memo } from 'react'
import styled, { css } from 'styled-components'
import { motion } from 'motion/react'
import { mq, presets, vw } from '@/styles'
import { currencyFormatter, LANG_COUNTRY } from '@/utils'
import { Img } from '../../base'
import { itemVariants } from './CartSummaryDropdown'
import { TypeCmsCartLine } from '@/schema'

type Props = {
  cartLine: TypeCmsCartLine
  id: number | string
  onClick?: () => void
  type?: string
}

const cartItemSizes = `(min-width: 768px) ${vw(55, 'desktop')}, ${vw(55, 'mobile')}`

export const CartSummaryItem = memo<Props>(({ cartLine, id, onClick, type = 'all' }) => {
  const { product, variant, quantity } = cartLine
  const { media, price } = variant
  const priceQuantity = price?.salePrice ? {
    salePrice: price.salePrice * quantity,
    retailPrice: price.retailPrice * quantity,
  } : null

  return (
    <DD
      variants={itemVariants}
      id={String(id)}
      {...(onClick && { ...onClick })}
      $type={type}
    >
      <article>
        <Img
          className={'product_image'}
          media={media?.[0]?.medium}
          lazy={false}
          sizes={cartItemSizes}
          quality={50}
        />
        <div className='info'>
          <div>
            <h5>
              {quantity} x {product?.name}
            </h5>
            <span className='total'>
              {priceQuantity &&
                currencyFormatter({
                  price: priceQuantity.salePrice,
                  langCountry: LANG_COUNTRY,
                  round: false,
                  trailingZeros: true,
                })}
            </span>
          </div>
          <div>
            {currencyFormatter({
              price: price?.salePrice,
              langCountry: LANG_COUNTRY,
              round: false,
              trailingZeros: true,
            })}
            /ud.
          </div>
        </div>
      </article>
    </DD>
  )
})

const DD = styled(motion.dd)<{ $type: string }>`
  background-color: var(--color-greylight);
  padding-bottom: ${vw(10, 'mobile')};
  min-height: ${vw(55, 'mobile')};
  overflow-x: hidden;
  transition: 300ms opacity ease;
  width: 100%;

  ${mq.greaterThan('nexus7')} {
    padding-bottom: ${vw(10, 'tablet')};
    min-height: ${vw(55, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    padding-bottom: ${vw(10, 'desktop')};
    min-height: ${vw(55, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    padding-bottom: 10px;
    min-height: 55px;
  }

  &:first-of-type {
    padding-top: ${vw(20.5, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding-top: ${vw(20.5, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding-top: ${vw(20.5, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding-top: 20.5px;
    }

    ${({ $type }) =>
      $type === 'new' &&
      css`
        border-top-left-radius: 20px;
        border-top-right-radius: 20px;
      `};
  }

  &:last-child {
    padding-bottom: ${vw(30, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding-bottom: ${vw(30, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding-bottom: ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding-bottom: 30px;
    }
  }

  article {
    align-items: center;
    display: flex;
    justify-content: flex-start;
    padding: 0 ${vw(20, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding: 0 ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding: 0 ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding: 0 20px;
    }
  }

  .info {
    align-items: flex-start;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    padding-left: ${vw(20, 'mobile')};
    width: 80%;

    ${mq.greaterThan('nexus7')} {
      padding-left: ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding-left: ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding-left: 20px;
    }

    > div {
      display: flex;
      flex-wrap: nowrap;
      width: 100%;

      h5,
      .total {
        width: auto;
        max-width: 100%;
      }

      h5 {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100%;
      }

      .total {
        font-weight: bold;
        flex-shrink: 0;
        margin-left: ${vw(20, 'mobile')};
        width: auto;

        ${mq.greaterThan('nexus7')} {
          margin-left: ${vw(20, 'tablet')};
        }

        ${mq.greaterThan('tablet')} {
          margin-left: ${vw(20, 'desktop')};
        }

        ${mq.greaterThan('desktop')} {
          margin-left: 20px;
        }
      }
    }

    h5,
    .total,
    span,
    div {
      ${presets.font12}
      color: var(--color-text);
      text-align: left;
    }

    span {
      font-weight: 500;
      width: 100%;
    }
  }

  .product__price {
    p {
      ${presets.font12}

      .value,
      .decimals,
      .unit {
        ${presets.font12}
        font-weight: 500;
      }
    }
  }

  .product_image {
    align-items: center;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    height: ${vw(55, 'mobile')};
    overflow: hidden;
    width: ${vw(55, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      height: ${vw(55, 'tablet')};
      width: ${vw(55, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(55, 'desktop')};
      width: ${vw(55, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 55px;
      width: 55px;
    }

    &.placeholder {
      background-color: rgba(0, 0, 0, 0.15);
    }

    img {
      height: ${vw(55, 'mobile')};
      object-fit: contain;
      object-position: center;
      width: ${vw(55, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        height: ${vw(55, 'tablet')};
        width: ${vw(55, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        height: ${vw(55, 'desktop')};
        width: ${vw(55, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        height: 55px;
        width: 55px;
      }
    }
  }

  .total {
    margin-bottom: 0;
  }
`
